import { cn } from '@/lib/utils';

/**
 * The parent panel's accent, in one place.
 *
 * **Indigo is this panel's colour**, taken from the rail it hangs off: the
 * sidebar is the deep indigo sheet `ConsoleLayout` paints for every
 * institute-side console, so the screens inside that frame are drawn from the
 * same hue rather than each one choosing its own. The product's default accent
 * is a teal-green, and a page that forgets this wrapper comes out green inside
 * an indigo frame — which is exactly how it has gone wrong before.
 *
 * The shared fields, panels, buttons and pagination on these pages paint
 * themselves from `--accent`, `--line` and `--text-*`, so redeclaring those on
 * the page root re-tints all of them without forking a single component
 * (CLAUDE.md §3). Arbitrary *property* utilities rather than a `style` object,
 * so this stays Tailwind-only and still switches with the theme (§7).
 *
 * The status family — `ok` / `warn` / `crit` / `info` — is deliberately left
 * alone: overdue work is red on every screen in the product, and a panel that
 * re-tinted it would be saying something different from the rest.
 *
 * One thing it cannot reach: `Select` renders its open panel into
 * `document.body`, outside this subtree, so a dropdown while it is open still
 * draws in the product's own palette.
 */
export const PARENT_PALETTE = cn(
  '[--accent:#4f46e5] [--accent-2:#4338ca] [--accent-soft:#eef2ff] [--accent-line:#c7d2fe]',
  '[--line:#e2e8f0] [--line-strong:#cbd5e1]',
  '[--text:#0f172a] [--text-2:#475569] [--text-3:#94a3b8]',
  '[--wash-a:rgba(79,70,229,0.09)] [--wash-b:rgba(129,140,248,0.07)]',

  'dark:[--accent:#818cf8] dark:[--accent-2:#a5b4fc] dark:[--accent-soft:#1e1b4b]',
  'dark:[--accent-line:#4338ca]',
  'dark:[--surface:#141530] dark:[--surface-2:#0f1026] dark:[--surface-3:#1e1f45]',
  'dark:[--line:#252a4a] dark:[--line-strong:#3b4270]',
  'dark:[--text:#e8eaf6] dark:[--text-2:#a5aacb] dark:[--text-3:#6f76a0]',
  'dark:[--wash-a:rgba(129,140,248,0.10)] dark:[--wash-b:rgba(99,102,241,0.07)]',

  '[&_.lbl]:text-slate-500 dark:[&_.lbl]:text-slate-400 [&_.lbl]:text-sm [&_.lbl]:font-semibold',
  '[&_label]:text-slate-500 dark:[&_label]:text-slate-400 [&_label]:text-sm [&_label]:font-semibold',
);
